import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  type ActiveFilterPart,
  ExportMenu,
  ListPageShell,
  PageHeader,
  Table,
  focusFirstFilter,
  listEmptyContent,
  listEmptyShellProps,
} from '~/components/data';
import { Button, DatePicker, Select } from '~/components/primitives';
import {
  DocStatusBadge,
  type DocStatusValue,
  StatusBadge,
  docStatusLabel,
  docStatusOptions,
} from '~/components/status';
import { fetchWarehouses } from '~/lib/api/inventory';
import { type StockEntryListFilters, fetchStockEntries } from '~/lib/api/stock-entries';
import { formatDate, formatDateTime, formatMoney } from '~/lib/format';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { STOCK_ENTRY_TYPE_LABELS, type StockEntryTypeValue, labelOf } from '~/lib/i18n/labels';
import { useActiveEntityStore } from '~/store/active-entity';

import shared from './_auth._shared.module.css';
import styles from './_auth.stock-entries.index.module.css';

export const Route = createFileRoute('/_auth/stock-entries/')({
  staticData: { minMainWidth: 960 },
  component: StockEntriesList,
});

function StockEntriesList(): JSX.Element {
  // FE-P2-1 (pass3 closure): status filter row.
  const [statusFilter, setStatusFilter] = useState<string>('');
  /*
   * F52 (WHS-F29/F30): фильтры по намерению поиска — тип, склад, период (контракт
   * B37). «Поступления за апрель по цеху» раньше найти было нельзя: был один статус.
   */
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [warehouseFilter, setWarehouseFilter] = useState<string>('');
  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const filters: StockEntryListFilters = {
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(typeFilter ? { type: typeFilter } : {}),
    ...(warehouseFilter ? { warehouse_id: warehouseFilter } : {}),
    ...(fromDate ? { from: fromDate } : {}),
    ...(toDate ? { to: toDate } : {}),
  };
  const anyFilter = Object.keys(filters).length > 0;
  const q = useQuery({
    queryKey: ['stock-entries', filters],
    queryFn: () => fetchStockEntries(filters),
  });
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: fetchWarehouses });
  const state = useListPageState(q);
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = q.data?.entities ?? {};
  const noWarehouses = q.data?.warehouse_scope?.size === 0;
  const filterRowRef = useRef<HTMLDivElement>(null);
  const resetFilters = (): void => {
    setTypeFilter('');
    setWarehouseFilter('');
    setFromDate(null);
    setToDate(null);
    setStatusFilter('');
    focusFirstFilter(filterRowRef.current);
  };
  /*
   * TCA-F2 (круг 1 гейта, UX) — «Показано 0 из 0» читалось размером реестра: пустое
   * состояние называет, сколько найдётся без отбора. Запрос — только когда пусто под
   * отбором; без отбора он совпадает с обычным и берётся из кэша.
   */
  const unfiltered = useQuery({
    queryKey: ['stock-entries', {}],
    queryFn: () => fetchStockEntries({}),
    enabled: anyFilter && state.kind === 'empty',
  });
  /*
   * TCA-F2 — отбор словами, теми же подписями, что у полей над таблицей: пустая выдача
   * под отбором называет, кого искали, а не выдаёт отбор за пустой склад.
   */
  const warehouseName =
    (warehouses.data?.items ?? []).find((w) => w.id === warehouseFilter)?.name ?? warehouseFilter;
  const activeFilters: ActiveFilterPart[] = [
    ...(typeFilter
      ? [
          {
            label: m.route_se_list_col_type(),
            value: labelOf(STOCK_ENTRY_TYPE_LABELS, typeFilter as StockEntryTypeValue),
          },
        ]
      : []),
    ...(warehouseFilter ? [{ label: m.route_se_list_col_warehouse(), value: warehouseName }] : []),
    ...(fromDate ? [{ label: m.route_se_list_filter_from(), value: formatDate(fromDate) }] : []),
    ...(toDate ? [{ label: m.route_se_list_filter_to(), value: formatDate(toDate) }] : []),
    ...(statusFilter
      ? [
          {
            label: m.route_se_list_col_status(),
            value: docStatusLabel(statusFilter as DocStatusValue),
          },
        ]
      : []),
  ];

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_stock_entries()}
        actions={
          <>
            <ExportMenu resource="stock-entries" filters={filters} />
            {/*
              F5 — приглашение создать документ убрано и ИЗ ШАПКИ, а не только
              из пустого состояния. Кладовщику без назначенных складов мастер
              всё равно не даст выбрать склад строки: кнопка вела бы в форму,
              которую нельзя дозаполнить. Это не «спрятать функцию», а не звать
              туда, где выхода нет.
            */}
            {!noWarehouses && (
              <Link to="/stock-entries/new">
                <Button leadingIcon={<Plus size={14} />}>{m.route_se_list_btn_new()}</Button>
              </Link>
            )}
          </>
        }
      />
      {/* FE-P2-1 (pass3 closure): inline filter row для list view. */}
      {/* F52 (WHS-F31): панель фильтров — строкой без карточной обёртки; рядом —
          «показано N из M» (B50), а не пустое место под одним селектом. */}
      {/* Гейт F59 (A-UI-1): у всех фильтров видимая подпись и одна высота 40 px —
          даты с подписями «С»/«По» стояли выше селектов без подписей. */}
      <div className={styles.filterRow} ref={filterRowRef}>
        <Select
          label={m.route_se_list_col_type()}
          value={typeFilter}
          options={[
            { value: '', label: m.route_se_list_filter_all_types() },
            ...(Object.keys(STOCK_ENTRY_TYPE_LABELS) as StockEntryTypeValue[]).map((t) => ({
              value: t,
              label: labelOf(STOCK_ENTRY_TYPE_LABELS, t),
            })),
          ]}
          onChange={(e) => setTypeFilter(e.target.value)}
        />
        <Select
          label={m.route_se_list_col_warehouse()}
          value={warehouseFilter}
          options={[
            { value: '', label: m.route_se_list_filter_all_warehouses() },
            ...(warehouses.data?.items ?? [])
              .filter((w) => !w.is_group)
              .map((w) => ({ value: w.id, label: w.name })),
          ]}
          onChange={(e) => setWarehouseFilter(e.target.value)}
        />
        <DatePicker
          label={m.route_se_list_filter_from()}
          value={fromDate}
          onChange={setFromDate}
          {...(toDate ? { max: toDate } : {})}
        />
        <DatePicker
          label={m.route_se_list_filter_to()}
          value={toDate}
          onChange={setToDate}
          {...(fromDate ? { min: fromDate } : {})}
        />
        <Select
          label={m.route_se_list_col_status()}
          value={statusFilter}
          placeholder={m.route_se_list_col_status()}
          options={[
            { value: '', label: m.so_filter_all_statuses() },
            // F2: значения опций — контракт `?status=` серверного реестра (пара B4),
            // подписи — тот же словарь, что и у бейджа строки.
            ...docStatusOptions(),
          ]}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
        {anyFilter && (
          <span className={styles.resetWrap}>
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              {m.list_filter_reset()}
            </Button>
          </span>
        )}
        {q.data && q.data.total !== null && (
          <span className={styles.shownCount}>
            {m.route_se_list_shown_of({
              shown: String(q.data.items.length),
              total: String(q.data.total),
            })}
          </span>
        )}
      </div>
      {/*
        F5 [WHS-F4] — пустота больше не маскирует отсутствие доступа.
        Кладовщик без назначенных складов видел «Документов пока нет. Создайте
        первый складской документ» — приглашение в путь, который его же область
        и не покажет. Теперь он читает правду про доступ, и кнопки нет: звать
        туда, где всё равно ничего не выйдет, — это тупик, а не помощь.
        `size === 0` — ни одного склада; `null` — ограничений нет вовсе.
      */}
      {/*
        TCA-F2 — под активным отбором пустая выдача называет отбор и предлагает его
        сбросить, а не создать «первый» документ. Без отбора — прежнее состояние
        первого запуска; без складов — правда про доступ при любом отборе (отбор
        тут ни при чём: область пуста сама по себе).
      */}
      <ListPageShell
        state={state}
        {...listEmptyShellProps(
          listEmptyContent({
            filters: noWarehouses ? [] : activeFilters,
            onResetFilters: resetFilters,
            unfilteredTotal: unfiltered.data?.total ?? undefined,
            empty: noWarehouses
              ? {
                  title: m.route_se_list_empty_scope_title(),
                  description: m.route_se_list_empty_scope_description(),
                }
              : {
                  title: m.route_se_list_empty_title(),
                  description: m.route_se_list_empty_description(),
                  action: (
                    <Link to="/stock-entries/new">
                      <Button variant="primary" leadingIcon={<Plus size={14} />}>
                        {m.route_se_list_btn_new()}
                      </Button>
                    </Link>
                  ),
                },
          }),
        )}
      >
        {(rows) => (
          <Table
            ariaLabel={m.nav_stock_entries()}
            data={rows}
            rowKey={(e) => e.id}
            stickyFirstColumn
            columns={[
              {
                id: 'id',
                header: m.common_column_id(),
                sticky: true,
                // FE-220 Pass 86: BE-M shipped voucher_no denorm — show «SE-2026-NNNN» вместо UUID prefix.
                accessor: (e) => (
                  <Link to="/stock-entries/$id" params={{ id: e.id }} className={shared.link}>
                    <span className={shared.mono}>
                      {(e as { voucher_no?: string | null }).voucher_no ?? e.id.slice(0, 8)}
                    </span>
                  </Link>
                ),
                mono: true,
              },
              ...(aggregateScope
                ? [
                    {
                      id: 'entity',
                      header: m.project_col_entity(),
                      accessor: (e: (typeof rows)[number]) =>
                        entities[e.entity_id ?? '']?.name ?? '—',
                      width: '180px',
                    },
                  ]
                : []),
              {
                // F52 (WHS-F30): склад — колонкой (паттерн WHS-F7). У перемещения — «откуда → куда».
                id: 'warehouse',
                header: m.route_se_list_col_warehouse(),
                accessor: (e) => {
                  const from = e.from_warehouse_name ?? null;
                  const to = e.to_warehouse_name ?? null;
                  if (from && to && from !== to) return `${from} → ${to}`;
                  return from ?? to ?? '—';
                },
              },
              {
                id: 'type',
                header: m.route_se_list_col_type(),
                accessor: (e) =>
                  labelOf(
                    STOCK_ENTRY_TYPE_LABELS,
                    e.entry_type as keyof typeof STOCK_ENTRY_TYPE_LABELS,
                  ),
              },
              {
                id: 'at',
                header: m.route_se_list_col_posting_at(),
                // F52 (WHS-F27): у черновика проведения не было — «—», а не дата создания.
                accessor: (e) => (e.status === 'draft' ? '—' : formatDateTime(e.posting_at)),
                mono: true,
              },
              {
                id: 'lines',
                header: m.route_se_list_col_lines(),
                accessor: (e) => e.lines_count,
                mono: true,
                align: 'right',
              },
              {
                id: 'value',
                header: m.route_se_list_col_value(),
                // F4: сумма приходит объектом «число + валюта» и печатается
                // денежным форматтером. Смешанные валюты строк дают `null` —
                // прочерк здесь честнее любого числа (§1 I3 решения).
                // WHS-B3: сумма документа с расходом по нулевой оценке — неполная.
                // Гейт F59 (A-UX-5): сумма отменённого документа приглушена — её не суммируют глазами.
                accessor: (e) =>
                  e.status === 'cancelled' ? (
                    <span className={styles.cancelledSum}>
                      {e.total_value ? formatMoney(e.total_value) : '—'}
                    </span>
                  ) : e.total_value ? (
                    formatMoney(e.total_value)
                  ) : (
                    '—'
                  ),
                mono: true,
                align: 'right',
              },
              {
                id: 'status',
                header: m.route_se_list_col_status(),
                // Гейт F59 (A-UX-1): проведённый по нулевой оценке — не «полный успех»: рядом
                // со статусом документа — предупреждение «себестоимость не определена».
                accessor: (e) => (
                  <span className={styles.statusCell}>
                    <DocStatusBadge status={e.status as DocStatusValue} size="sm" />
                    {e.valued_at_zero && (
                      <StatusBadge
                        status="needs_review"
                        size="sm"
                        label={m.se_valued_at_zero_chip()}
                      />
                    )}
                  </span>
                ),
              },
            ]}
          />
        )}
      </ListPageShell>
    </div>
  );
}
